import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { profile, competencies, getProjectsChronological } from '../data/mock';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import SimulationBg from '../components/SimulationBg';
import {
  ArrowRight,
  MapPin,
  PencilRuler,
  Waves,
  Cog,
  Terminal,
  Circle,
  Layers,
  FolderKanban,
  Compass,
} from 'lucide-react';

const iconMap = { PencilRuler, Waves, Cog, Terminal };

const accentMap = {
  steel:   { text: 'text-[#4A8FD1]', bg: 'bg-[#4A8FD1]/10', border: 'hover:border-[#4A8FD1]', dot: 'bg-[#4A8FD1]' },
  cyan:    { text: 'text-[#4BADC8]', bg: 'bg-[#4BADC8]/10', border: 'hover:border-[#4BADC8]', dot: 'bg-[#4BADC8]' },
  amber:   { text: 'text-[#E4A135]', bg: 'bg-[#E4A135]/10', border: 'hover:border-[#E4A135]', dot: 'bg-[#E4A135]' },
  green:   { text: 'text-[#4CB88A]', bg: 'bg-[#4CB88A]/10', border: 'hover:border-[#4CB88A]', dot: 'bg-[#4CB88A]' },
  magenta: { text: 'text-[#D95871]', bg: 'bg-[#D95871]/10', border: 'hover:border-[#D95871]', dot: 'bg-[#D95871]' },
};

const Home = () => {
  const { t, language } = useLanguage();
  const featured = getProjectsChronological('desc').slice(0, 3);

  return (
    <div>
      {/* HERO */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 thermal-wash pointer-events-none" />
        <div className="absolute inset-0 mesh-fine opacity-70 pointer-events-none" />
        <SimulationBg variant="streamlines" opacity={0.55} />
        {/* Thin CFD legend stripe at very top */}
        <div className="absolute top-0 left-0 right-0 h-[2px] cfd-bar" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="max-w-4xl">
            {/* Availability chip */}
            <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full bg-[#151B27]/85 backdrop-blur border border-white/10 text-xs mono">
              <Circle className="w-2 h-2 fill-emerald-500 stroke-emerald-500" />
              <span className="uppercase tracking-widest text-slate-300">
                {t('home.available')}
              </span>
            </div>

            {/* Subtle kicker — badge line */}

            {/* Name — LARGEST element on the page */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] font-extrabold text-slate-50 leading-[0.95] tracking-tight">
              {profile.name}
            </h1>

            {/* Role / title — secondary hierarchy */}
            <p className="mt-5 text-2xl sm:text-3xl lg:text-4xl font-semibold text-[#4A8FD1] tracking-tight">
              {t('home.title')}
            </p>

            {/* Subtitle */}
            <p className="mt-3 text-base sm:text-lg text-slate-300 leading-snug max-w-3xl">
              {t('home.subtitle')}
            </p>

            {/* Value line */}
            <p className="mt-6 text-sm sm:text-base text-slate-400 italic leading-relaxed max-w-3xl border-l-2 border-[#4A8FD1] pl-4">
              {t('home.value')}
            </p>

            {/* Short intro */}
            <p className="mt-5 text-sm text-slate-400 leading-relaxed max-w-3xl">
              {t('home.intro')}
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/projects">
                <Button size="lg" className="bg-[#4A8FD1] hover:bg-[#6AAFE0] text-white rounded-md h-12 px-6">
                  {t('home.heroCTA')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline" className="rounded-md h-12 px-6 border-white/15 bg-transparent text-slate-200 hover:bg-white/5 hover:border-[#4A8FD1] hover:text-[#4A8FD1]">
                  {t('home.heroSecondary')}
                </Button>
              </Link>
            </div>

            {/* Tools strip — prominent, readable, well-spaced */}
            <div className="mt-14">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xs mono uppercase tracking-[0.3em] text-[#4A8FD1] font-semibold">
                  {t('home.toolsLabel')}
                </span>
                <span className="flex-1 h-px bg-gradient-to-r from-[#4A8FD1]/40 to-transparent" />
              </div>
              <div className="flex flex-wrap gap-2.5">
                {['ANSYS', 'CalculiX', 'PrePoMax', 'FreeCAD', 'Inventor', 'Python'].map((tool) => (
                  <span
                    key={tool}
                    className="inline-flex items-center px-4 py-2 rounded-md bg-[#1A2231] border border-white/15 text-base mono font-medium text-slate-100 hover:border-[#4A8FD1] hover:bg-[#1F2838] transition-colors"
                  >
                    {tool}
                  </span>
                ))}
              </div>
            </div>

            {/* Meta strip — readable */}
            <div className="mt-12 flex flex-wrap gap-x-6 gap-y-3 text-sm">
              <div className="flex items-center gap-2 text-slate-300">
                <MapPin className="w-4 h-4 text-[#4A8FD1]" />
                <span className="mono">{profile.location[language]}</span>
              </div>
              <span className="text-slate-600 hidden sm:inline" aria-hidden="true">·</span>
              <div className="flex items-center gap-2 text-slate-300 mono">
                M.Sc. TU Ilmenau
              </div>
              <span className="text-slate-600 hidden sm:inline" aria-hidden="true">·</span>
              <div className="flex items-center gap-2 text-slate-300 mono">
                FEM / CFD / CAD
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPETENCIES */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="max-w-3xl mb-14">
            <div className="flex items-center gap-3 text-xs mono uppercase tracking-widest text-[#4A8FD1] mb-3">
              <span>01</span>
              <Layers className="w-4 h-4" />
              <span className="w-8 h-px bg-[#4A8FD1]" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
              {t('home.competenciesTitle')}
            </h2>
            <p className="mt-3 text-slate-400 text-base sm:text-lg">
              {t('home.competenciesSub')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {competencies.map((c, idx) => {
              const Icon = iconMap[c.icon] || Cog;
              const a = accentMap[c.accent] || accentMap.steel;
              return (
                <div
                  key={c.id}
                  className={`group relative p-6 bg-[#1A2231] border border-white/10 rounded-lg hover:shadow-md transition-all duration-300 ${a.border}`}
                >
                  {/* top accent bar */}
                  <div className={`absolute top-0 left-0 right-0 h-[2px] ${a.dot} opacity-80 rounded-t-lg`} />
                  <div className="flex items-start justify-between mb-5">
                    <div className={`w-11 h-11 rounded-md ${a.bg} flex items-center justify-center transition-colors`}>
                      <Icon className={`w-5 h-5 ${a.text}`} />
                    </div>
                    <span className="text-xs mono text-slate-400">
                      0{idx + 1}
                    </span>
                  </div>
                  <h3 className="text-base font-semibold text-slate-50 mb-2">
                    {c.title[language]}
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed mb-4">
                    {c.desc[language]}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {c.tools.map((tool) => (
                      <span
                        key={tool}
                        className="inline-block text-[10px] mono uppercase tracking-wider px-2 py-0.5 bg-white/5 text-slate-400 border border-white/10 rounded"
                      >
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FEATURED PROJECTS */}
      <section className="relative py-20 lg:py-28 sky-section border-y border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-14 gap-4">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 text-xs mono uppercase tracking-widest text-[#4BADC8] mb-3">
                <span>02</span>
                <FolderKanban className="w-4 h-4" />
                <span className="w-8 h-px bg-[#4BADC8]" />
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight">
                {t('home.featuredTitle')}
              </h2>
              <p className="mt-3 text-slate-400 text-base sm:text-lg">
                {t('home.featuredSub')}
              </p>
            </div>
            <Link to="/projects" className="inline-flex items-center gap-2 text-sm font-medium text-[#4A8FD1] hover:gap-3 transition-all">
              {t('home.viewAll')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p) => (
              <Link
                key={p.id}
                to={`/projects/${p.id}`}
                className="project-card group block bg-[#1A2231] border border-white/10 rounded-lg overflow-hidden hover:border-[#4A8FD1] hover:shadow-lg transition-all duration-300"
              >
                <div className="aspect-[16/10] overflow-hidden bg-white/10">
                  <img
                    src={p.thumbnail}
                    alt={p.title[language]}
                    className="project-thumb w-full h-full object-cover"
                  />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-3 text-[10px] mono uppercase tracking-widest text-slate-500 mb-2">
                    <span>{p.year}</span>
                    <span className="w-1 h-1 rounded-full bg-slate-300" />
                    <span>{p.context[language]}</span>
                  </div>
                  <h3 className="text-base font-semibold text-slate-50 leading-snug group-hover:text-[#4A8FD1] transition-colors">
                    {p.title[language]}
                  </h3>
                  <p className="mt-2 text-sm text-slate-400 line-clamp-2 leading-relaxed">
                    {p.summary[language]}
                  </p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {p.tools.slice(0, 3).map((tool) => (
                      <Badge
                        key={tool}
                        variant="secondary"
                        className="text-[10px] mono uppercase tracking-wider bg-white/10 text-slate-300 hover:bg-white/10 font-medium"
                      >
                        {tool}
                      </Badge>
                    ))}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FOCUS */}
      <section className="relative py-20 lg:py-24 overflow-hidden">
        <div className="absolute inset-0 thermal-wash-soft pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="flex items-center gap-3 text-xs mono uppercase tracking-widest text-[#E4A135] mb-3">
            <span>03</span>
            <Compass className="w-4 h-4" />
            <span className="w-8 h-px bg-[#E4A135]" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-50 tracking-tight mb-6">
            {t('home.focusTitle')}
          </h2>
          <p className="text-lg sm:text-xl text-slate-400 leading-relaxed">
            {t('home.focusText')}
          </p>
          <div className="mt-8">
            <Link to="/about">
              <Button variant="outline" className="rounded-md border-white/15 hover:border-[#E4A135] hover:text-[#E4A135]">
                {t('nav.about')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
